import { portalDealsFrom } from '../ceoBrief/briefDeals';
import type { BriefDealsPayload } from '../ceoBrief/briefDeals';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Talking to the read_brief_portal database function.
 *
 * The token in the URL is the entire credential — the reader is not signed in
 * and never will be. Nothing here holds state between calls and every call
 * re-sends it: there is no session to put it in, and inventing one in
 * localStorage would be a session with no expiry that the server does not
 * know about. Same contract as reviewApi, for the same reason.
 *
 * The ordinary browser client carries the publishable anon key. The database
 * function grants EXECUTE to anon and nothing else, then validates this token
 * inside use_brief_token, which remains the single place revocation is
 * decided. There is no edge-function deploy beside the app deploy anymore.
 */

export type BriefPortalErrorCode = 'not_found' | 'bad_request' | 'unreachable';

export class BriefPortalError extends Error {
  code: BriefPortalErrorCode;
  constructor(code: BriefPortalErrorCode) {
    super(code);
    this.code = code;
  }
}

/**
 * What each failure is called in front of somebody with no account.
 *
 * Deliberately not "invalid token" or a status code. The reader has a link and
 * no context; every message here says what to do next, and none of them
 * suggests the reader did something wrong.
 */
const MESSAGES: Record<BriefPortalErrorCode, string> = {
  not_found: 'This link is no longer active. Ask Kyu for a new one.',
  bad_request: 'This link is not complete. Check it was copied in full.',
  unreachable: 'The brief could not be loaded just now. Try again in a moment.',
};

export function briefPortalErrorMessage(code: BriefPortalErrorCode): string {
  return MESSAGES[code];
}

export interface PortalBrief {
  brief_date: string;
  body: string;
  updated_at: string | null;
}

/** One starred deal, as the server sends it. The note is not in here. */
export interface BriefPortalPayload {
  /** Null when the link is live and nothing has been written yet. */
  brief: PortalBrief | null;
  /** Newest first. Empty only when there are no briefs at all. */
  dates: string[];
  /**
   * The starred-deals appendix, live rather than snapshotted — the same rule
   * the Hub prints it by. Empty when nothing is starred, or when the server
   * could not read the board: a brief with no appendix beats one with an
   * error under it.
   */
  deals: BriefDealsPayload;
}

/**
 * One brief and the dates available, for a token.
 *
 * `date` is a request, not a filter. The server falls back to the newest when
 * it does not recognise the day, so a stale bookmark shows today's brief
 * rather than an empty page that looks broken.
 */
export async function loadBrief(
  client: SupabaseClient,
  token: string,
  date?: string,
): Promise<BriefPortalPayload> {
  const args: { p_token: string; p_date?: string } = { p_token: token };
  if (date !== undefined && date !== '') {
    args.p_date = date;
  }

  const result = (await client.rpc('read_brief_portal', args)) as {
    data: unknown;
    error: unknown;
  };

  // A database failure is temporary from the reader's point of view. Token
  // failures are deliberately SQL null instead, so an unknown token and a
  // revoked one never become distinguishable from outside.
  if (result.error !== null && result.error !== undefined) {
    throw new BriefPortalError('unreachable');
  }

  const payload = result.data as {
    brief?: unknown;
    dates?: unknown;
    deals?: unknown;
  } | null;
  if (payload === null) {
    throw new BriefPortalError('not_found');
  }

  const dates = Array.isArray(payload.dates)
    ? payload.dates.filter((one): one is string => typeof one === 'string')
    : [];

  const brief = payload.brief as PortalBrief | null | undefined;
  return {
    brief:
      brief !== null && brief !== undefined && typeof brief.brief_date === 'string'
        ? { brief_date: brief.brief_date, body: brief.body, updated_at: brief.updated_at ?? null }
        : null,
    dates,
    deals: portalDealsFrom(payload.deals),
  };
}
